import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useIsMobile } from '../hooks/useIsMobile';
import { useUserPresence, formatPresence } from '../hooks/useUserPresence';
import {
  getOrCreateDmRoom, getPublicProfile, getUserByPhone, getMyDmRooms,
  followUser, unfollowUser,
  blockUser, unblockUser, getBlockedUsers,
  muteUser, unmuteUser, getMutedUsers,
  submitReport,
  searchUsers,
} from '../api/client';
import type { DmRoomInfo, UserSearchResult } from '../api/client';
import type { User } from '../types';
import { useStreamChat } from '../contexts/StreamContext';
import {
  MessageCircle, MessageSquare, Send, PenSquare, Search, X, ArrowLeft,
  PanelRightClose, PanelRightOpen, Check, UserPlus,
  Bell, BellOff, ShieldOff, Flag,
} from 'lucide-react';
import type { Channel, Event } from 'stream-chat';
import { UserAvatar } from '../components/UserAvatar';

// ── Palette (mirror of the Snagg-native chat redesign) ─────────────────
const C = {
  bg: '#F4F3F8',
  surface: '#FFFFFF',
  surfaceAlt: '#FAFAFC',
  hover: '#F4F3F8',
  active: '#F1EDFB',
  border: '#E8E6F0',
  borderSoft: '#EFEEF3',
  ink: '#1C2033',
  text: '#353949',
  textMuted: '#757783',
  textFaint: '#9A9CA5',
  brand: '#5F3DC4',
  brandSoft: '#F1EDFB',
  bubbleThem: '#F1F0F5',
  composerBg: '#F4F3F8',
  online: '#22C55E',
  danger: '#FF2D55',
} as const;

interface DmSummary {
  channelId: string;
  participantUserId: string | null;
  participantName: string;
  participantAvatar: string | null;
  lastMessage: string;
  lastMessageTs: number;
  lastSenderIsMe: boolean;
  unreadCount: number;
}

interface ChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  content: string;
  timestamp: number;
  isOwn: boolean;
}

type ClusterPos = 'solo' | 'first' | 'mid' | 'last';
type ThreadItem =
  | { type: 'date'; key: string; label: string }
  | { type: 'unread'; key: string }
  | { type: 'msg'; key: string; msg: ChatMessage; pos: ClusterPos };

export function InboxPage() {
  useDocumentTitle('Messages');
  const isMobile = useIsMobile();
  const { client, isReady } = useStreamChat();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedChannelId = searchParams.get('room');
  const composeRecipientId = searchParams.get('recipientId');

  const [conversations, setConversations] = useState<DmSummary[]>([]);
  const [selectedMessages, setSelectedMessages] = useState<ChatMessage[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showProfilePanel, setShowProfilePanel] = useState(true);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Compose modal — autocomplete-driven new-message picker
  const [composeParticipant, setComposeParticipant] = useState<{ name: string; avatar: string | null } | null>(null);
  const [showCompose, setShowCompose] = useState(false);
  const [composeQuery, setComposeQuery] = useState('');
  const [composeResults, setComposeResults] = useState<UserSearchResult[]>([]);
  const [composeSearching, setComposeSearching] = useState(false);
  const [composeSelected, setComposeSelected] = useState<UserSearchResult | null>(null);
  const [composeError, setComposeError] = useState<string | null>(null);
  const [composeStarting, setComposeStarting] = useState(false);

  const resetCompose = () => {
    setComposeQuery('');
    setComposeResults([]);
    setComposeSearching(false);
    setComposeSelected(null);
    setComposeError(null);
    setComposeStarting(false);
  };

  // Debounced username search — 400ms after the last keystroke, ≥2 chars
  useEffect(() => {
    if (!showCompose || composeSelected) { setComposeResults([]); setComposeSearching(false); return; }
    const q = composeQuery.trim();
    if (q.length < 2) { setComposeResults([]); setComposeSearching(false); return; }
    setComposeSearching(true);
    let cancelled = false;
    const t = window.setTimeout(() => {
      searchUsers(q).then(rs => { if (!cancelled) setComposeResults(rs); })
        .catch(() => { if (!cancelled) setComposeResults([]); })
        .finally(() => { if (!cancelled) setComposeSearching(false); });
    }, 400);
    return () => { cancelled = true; window.clearTimeout(t); };
  }, [composeQuery, showCompose, composeSelected]);

  const platformDmRoomsRef = useRef<Map<string, DmRoomInfo>>(new Map());

  // ── Selected participant: name → public profile (isSeller, followerCount) ──
  const selectedConvo = conversations.find(c => c.channelId === selectedChannelId);
  const selectedParticipantName = selectedConvo?.participantName || composeParticipant?.name || null;
  const [participantProfile, setParticipantProfile] = useState<User | null>(null);

  useEffect(() => {
    if (!selectedParticipantName) { setParticipantProfile(null); return; }
    let cancelled = false;
    getPublicProfile(selectedParticipantName)
      .then(p => { if (!cancelled) setParticipantProfile(p); })
      .catch(() => { if (!cancelled) setParticipantProfile(null); });
    return () => { cancelled = true; };
  }, [selectedParticipantName]);

  // ── Compose ─────────────────────────────────────────────────────────
  // Search-bar-as-phone fallback: if the query looks like a phone number,
  // try an exact phone lookup so existing users are reachable that way too.
  const phoneFallback = async (raw: string): Promise<UserSearchResult | null> => {
    if (!/^\+?\d[\d\s().-]{6,}$/.test(raw)) return null;
    const phone = raw.replace(/[\s().-]/g, '');
    const phoneWithCountry = phone.startsWith('+') ? phone : `+1${phone}`;
    try {
      const u = await getUserByPhone(phoneWithCountry);
      return { id: u.id, username: u.username, avatarUrl: u.avatarUrl, isSeller: u.isSeller };
    } catch { return null; }
  };

  const startWith = (user: UserSearchResult) => {
    setComposeParticipant({ name: user.username, avatar: user.avatarUrl });
    setShowCompose(false);
    setSearchParams({ recipientId: user.id });
    setTimeout(resetCompose, 200);
  };

  const handleComposeStart = async () => {
    if (composeSelected) { startWith(composeSelected); return; }
    setComposeStarting(true);
    const match = await phoneFallback(composeQuery.trim());
    setComposeStarting(false);
    if (match) startWith(match);
    else setComposeError('Pick someone from the list, or enter a valid phone number.');
  };

  // ── Platform DM rooms (for participant name resolution) ──────────────
  useEffect(() => {
    getMyDmRooms().then((rooms) => {
      const map = new Map<string, DmRoomInfo>();
      for (const r of rooms) map.set(r.channelId, r);
      platformDmRoomsRef.current = map;
      if (client && isReady) buildConversationList();
    }).catch(() => {});
  }, [client, isReady]);

  // ── Conversation list build ─────────────────────────────────────────
  const buildConversationList = useCallback(async () => {
    if (!client) return;

    try {
      const channels = await client.queryChannels(
        { type: 'messaging', members: { $in: [client.userID!] } },
        { last_message_at: -1 },
        { limit: 30, watch: false }
      );

      const dms: DmSummary[] = channels.map((channel) => {
        const channelId = channel.id || '';
        const platformInfo = platformDmRoomsRef.current.get(channelId);

        let participantUserId: string | null = null;
        let participantName = 'Unknown';
        let participantAvatar: string | null = null;

        const members = Object.values(channel.state.members);
        const other = members.find(m => m.user_id !== client.userID);
        if (other?.user_id) participantUserId = other.user_id;

        if (platformInfo?.participantUsername) {
          participantName = platformInfo.participantUsername;
          participantAvatar = platformInfo.participantAvatarUrl || null;
        } else if (other?.user) {
          participantName = other.user.name || other.user.id || 'Unknown';
          participantAvatar = (other.user.image as string) || null;
        }

        const lastMsg = channel.state.messages[channel.state.messages.length - 1];
        return {
          channelId,
          participantUserId,
          participantName,
          participantAvatar,
          lastMessage: lastMsg?.text || '',
          lastMessageTs: lastMsg?.created_at ? new Date(lastMsg.created_at).getTime() : 0,
          lastSenderIsMe: lastMsg?.user?.id === client.userID,
          unreadCount: channel.countUnread(),
        };
      });

      setConversations(dms);
    } catch (err) {
      console.error('[Inbox] Failed to query channels:', err);
    }
  }, [client]);

  useEffect(() => {
    if (isReady && client) buildConversationList();
  }, [isReady, client, buildConversationList]);

  useEffect(() => {
    if (!client) return;
    const subs = [
      client.on('notification.message_new', () => buildConversationList()),
      client.on('notification.added_to_channel', () => buildConversationList()),
      client.on('notification.mark_read', () => buildConversationList()),
      client.on('message.new', () => buildConversationList()),
      client.on('message.read', () => buildConversationList()),
    ];
    return () => { subs.forEach(s => s.unsubscribe()); };
  }, [client, buildConversationList]);

  // ── Selected channel load ───────────────────────────────────────────
  useEffect(() => {
    if (!client || !selectedChannelId) {
      setSelectedChannel(null);
      setSelectedMessages([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const channel = client.channel('messaging', selectedChannelId);
        await channel.watch();
        if (cancelled) return;
        setSelectedChannel(channel);
        loadMessagesFromChannel(channel);
        channel.markRead().catch(() => {});
      } catch (err) {
        console.error('[Inbox] Failed to load channel:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [client, selectedChannelId]);

  useEffect(() => {
    if (!composeRecipientId) return;
    setSelectedChannel(null);
    setSelectedMessages([]);
  }, [composeRecipientId]);

  useEffect(() => {
    if (!selectedChannel) return;
    const handler = selectedChannel.on('message.new', (event: Event) => {
      const msg = event.message;
      if (!msg || !msg.text) return;
      const chatMsg = streamMsgToChatMessage(msg);
      if (chatMsg) {
        setSelectedMessages(prev => prev.some(m => m.id === chatMsg.id) ? prev : [...prev, chatMsg]);
      }
      if (msg.user?.id && msg.user.id !== client?.userID) {
        selectedChannel.markRead().catch(() => {});
      }
    });
    return () => { handler.unsubscribe(); };
  }, [selectedChannel, client]);

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
  }, [selectedMessages, selectedChannelId]);

  function loadMessagesFromChannel(channel: Channel) {
    const msgs = (channel.state.messages || [])
      .filter(m => m.text)
      .map(streamMsgToChatMessage)
      .filter(Boolean) as ChatMessage[];
    setSelectedMessages(msgs);
  }

  function streamMsgToChatMessage(msg: {
    id: string; text?: string;
    user?: { id: string; name?: string } | null;
    created_at?: string | Date;
  }): ChatMessage | null {
    if (!msg.text) return null;
    const ts = msg.created_at ? new Date(msg.created_at).getTime() : 0;
    return {
      id: msg.id,
      senderId: msg.user?.id || '',
      senderName: msg.user?.name || msg.user?.id || 'Unknown',
      content: msg.text,
      timestamp: ts,
      isOwn: msg.user?.id === client?.userID,
    };
  }

  const handleSendMessage = async () => {
    if (!newMessage.trim() || isSending) return;
    setIsSending(true);
    try {
      let ch = selectedChannel;
      if (!ch && composeRecipientId && client) {
        const { channelId } = await getOrCreateDmRoom(composeRecipientId);
        ch = client.channel('messaging', channelId);
        await ch.watch();
        setSelectedChannel(ch);
        setSearchParams({ room: channelId });
        await buildConversationList();
      }
      if (!ch) return;
      await ch.sendMessage({ text: newMessage.trim() });
      setNewMessage('');
    } catch (err) {
      console.error('Failed to send:', err);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatRelative = (ts: number) => {
    if (!ts) return '';
    const diff = Date.now() - ts;
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d`;
    return new Date(ts).toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // ── Layout selection ────────────────────────────────────────────────
  const showSidebar = !isMobile || !selectedChannelId;
  const showThread = !isMobile || !!selectedChannelId || !!composeRecipientId;
  const showPanel = showProfilePanel && !isMobile && (selectedChannel || composeRecipientId);

  return (
    <Layout>
      <div style={{ background: C.bg, padding: 24, minHeight: 'calc(100vh - 64px)' }}>
        <div style={{
          height: 'calc(100vh - 112px)',
          display: 'flex',
          background: C.surface,
          borderRadius: 16,
          border: `1px solid ${C.border}`,
          overflow: 'hidden',
          minHeight: 0,
        }}>
          {showSidebar && (
            <InboxRail
              isMobile={isMobile}
              isReady={isReady}
              conversations={conversations}
              selectedChannelId={selectedChannelId}
              onSelect={(id) => setSearchParams({ room: id })}
              onNewMessage={() => setShowCompose(true)}
              formatRelative={formatRelative}
            />
          )}

          {showThread && (selectedChannel || composeRecipientId) ? (
            <Thread
              messages={selectedMessages}
              participantProfile={participantProfile}
              fallbackName={selectedConvo?.participantName || composeParticipant?.name || ''}
              fallbackAvatar={selectedConvo?.participantAvatar || composeParticipant?.avatar || null}
              participantUserId={selectedConvo?.participantUserId || composeRecipientId || null}
              newMessage={newMessage}
              setNewMessage={setNewMessage}
              isSending={isSending}
              onSend={handleSendMessage}
              onKeyDown={handleKeyPress}
              messagesContainerRef={messagesContainerRef}
              isMobile={isMobile}
              onBack={() => setSearchParams({})}
              profileOpen={showProfilePanel}
              onToggleProfile={() => setShowProfilePanel(v => !v)}
            />
          ) : !isMobile ? (
            <EmptyThreadHero hasConvos={conversations.length > 0} />
          ) : null}

          {showPanel && (
            <ProfilePanel
              profile={participantProfile}
              fallbackName={selectedConvo?.participantName || composeParticipant?.name || ''}
              fallbackAvatar={selectedConvo?.participantAvatar || composeParticipant?.avatar || null}
              participantUserId={selectedConvo?.participantUserId || composeRecipientId || null}
              onClose={() => setShowProfilePanel(false)}
            />
          )}
        </div>

        {showCompose && (
          <ComposeModal
            query={composeQuery}
            onQueryChange={(v) => { setComposeQuery(v); setComposeError(null); }}
            results={composeResults}
            searching={composeSearching}
            selected={composeSelected}
            onSelect={setComposeSelected}
            onClearSelected={() => { setComposeSelected(null); setComposeQuery(''); }}
            onClose={() => { setShowCompose(false); resetCompose(); }}
            onStart={handleComposeStart}
            starting={composeStarting}
            error={composeError}
          />
        )}
      </div>
    </Layout>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Inbox rail
// ──────────────────────────────────────────────────────────────────────
function InboxRail({
  isMobile, isReady, conversations, selectedChannelId, onSelect, onNewMessage, formatRelative,
}: {
  isMobile: boolean;
  isReady: boolean;
  conversations: DmSummary[];
  selectedChannelId: string | null;
  onSelect: (id: string) => void;
  onNewMessage: () => void;
  formatRelative: (ts: number) => string;
}) {
  return (
    <div style={{
      width: isMobile ? '100%' : 340,
      flexShrink: 0,
      borderRight: isMobile ? 'none' : `1px solid ${C.border}`,
      display: 'flex', flexDirection: 'column', minHeight: 0,
    }}>
      <div style={{
        padding: '18px 16px 12px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <h2 style={{ margin: 0, fontSize: 19, fontWeight: 700, color: C.ink, letterSpacing: '-0.01em' }}>Messages</h2>
        <button
          onClick={onNewMessage}
          title="New message"
          style={{
            width: 32, height: 32, borderRadius: 8, border: 'none',
            background: C.brandSoft, color: C.brand, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <PenSquare size={16} />
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {!isReady ? (
          <div style={{ padding: 40, textAlign: 'center', color: C.textMuted, fontSize: 13 }}>
            Connecting to chat…
          </div>
        ) : conversations.length === 0 ? (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: C.textMuted }}>
            <MessageCircle size={48} color="#D8D6E0" />
            <p style={{ marginTop: 12, fontSize: 14, color: C.text }}>No messages yet</p>
            <p style={{ fontSize: 13, color: C.textFaint, marginTop: 4 }}>Your conversations will appear here</p>
          </div>
        ) : conversations.map(c => (
          <ConvoRow
            key={c.channelId}
            convo={c}
            active={c.channelId === selectedChannelId}
            onClick={() => onSelect(c.channelId)}
            formatRelative={formatRelative}
          />
        ))}
      </div>
    </div>
  );
}

function ConvoRow({ convo, active, onClick, formatRelative }: {
  convo: DmSummary; active: boolean; onClick: () => void; formatRelative: (ts: number) => string;
}) {
  const hasUnread = convo.unreadCount > 0;
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%', textAlign: 'left', border: 'none',
        background: active ? C.active : 'transparent',
        padding: '12px 14px 12px 16px',
        display: 'flex', gap: 12, alignItems: 'center',
        cursor: 'pointer',
        borderLeft: `3px solid ${active ? C.brand : 'transparent'}`,
        transition: 'background 0.12s',
      }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = C.hover; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      <UserAvatar src={convo.participantAvatar} alt={convo.participantName} size={42} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
          <span style={{
            fontSize: 14, fontWeight: hasUnread ? 700 : 600, color: C.ink,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            flex: 1, minWidth: 0,
          }}>{convo.participantName}</span>
          <span style={{
            fontSize: 11.5, fontWeight: hasUnread ? 600 : 400,
            color: hasUnread ? C.brand : C.textFaint, flexShrink: 0,
          }}>{formatRelative(convo.lastMessageTs)}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            fontSize: 13, color: hasUnread ? C.text : C.textMuted,
            fontWeight: hasUnread ? 500 : 400,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            flex: 1, minWidth: 0, lineHeight: 1.4,
          }}>
            {convo.lastSenderIsMe && <span style={{ color: C.textFaint, fontWeight: 400 }}>You: </span>}
            {convo.lastMessage}
          </span>
          {hasUnread && (
            <span style={{
              flexShrink: 0,
              minWidth: 18, height: 18, padding: '0 5px',
              borderRadius: 99, background: C.brand,
              color: '#fff', fontSize: 10.5, fontWeight: 700,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}>{convo.unreadCount}</span>
          )}
        </div>
      </div>
    </button>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Thread
// ──────────────────────────────────────────────────────────────────────
function Thread({
  messages, participantProfile, fallbackName, fallbackAvatar, participantUserId,
  newMessage, setNewMessage, isSending, onSend, onKeyDown, messagesContainerRef,
  isMobile, onBack, profileOpen, onToggleProfile,
}: {
  messages: ChatMessage[];
  participantProfile: User | null;
  fallbackName: string;
  fallbackAvatar: string | null;
  participantUserId: string | null;
  newMessage: string;
  setNewMessage: (v: string) => void;
  isSending: boolean;
  onSend: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  messagesContainerRef: React.RefObject<HTMLDivElement | null>;
  isMobile: boolean;
  onBack: () => void;
  profileOpen: boolean;
  onToggleProfile: () => void;
}) {
  const name = participantProfile?.username || fallbackName || 'User';
  const avatar = participantProfile?.avatarUrl || fallbackAvatar || null;
  const isSeller = participantProfile?.isSeller ?? false;
  const presence = useUserPresence(participantUserId);
  const presenceLabel = formatPresence(presence);
  const items = clusterize(messages);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
      {/* Header */}
      <div style={{
        padding: '14px 22px',
        borderBottom: `1px solid ${C.border}`,
        background: C.surface,
        display: 'flex', alignItems: 'center', gap: 12,
        flexShrink: 0,
      }}>
        {isMobile && (
          <button
            onClick={onBack}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: C.brand, display: 'flex' }}
          >
            <ArrowLeft size={20} />
          </button>
        )}
        <div style={{ position: 'relative' }}>
          <UserAvatar src={avatar} alt={name} size={40} />
          {presence.isOnline && <PresenceDot size={12} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: C.ink, letterSpacing: '-0.01em' }}>{name}</span>
            {isSeller && <SellerPill />}
          </div>
          {presenceLabel && (
            <div style={{
              fontSize: 12, marginTop: 2, fontWeight: 500,
              color: presence.isOnline ? C.online : C.textFaint,
              display: 'flex', alignItems: 'center', gap: 5,
            }}>
              {presence.isOnline && <span style={{ width: 6, height: 6, borderRadius: 99, background: C.online, display: 'inline-block' }} />}
              {presenceLabel}
            </div>
          )}
        </div>
        {!isMobile && (
          <button
            onClick={onToggleProfile}
            title={profileOpen ? 'Hide details' : 'Show details'}
            style={{
              width: 36, height: 36, borderRadius: 8, border: 'none',
              background: profileOpen ? C.brandSoft : 'transparent',
              color: profileOpen ? C.brand : '#5C5E6C',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {profileOpen ? <PanelRightClose size={18} /> : <PanelRightOpen size={18} />}
          </button>
        )}
      </div>

      {/* Messages */}
      <div ref={messagesContainerRef} style={{
        flex: 1, overflowY: 'auto', padding: '4px 0 10px',
        background: C.surfaceAlt,
      }}>
        {items.length === 0 ? (
          <ThreadEmpty name={name} avatar={avatar} />
        ) : (
          items.map(it => {
            if (it.type === 'date') return <DateSep key={it.key} label={it.label} />;
            if (it.type === 'unread') return <UnreadSep key={it.key} />;
            const showAvatar = !it.msg.isOwn && (it.pos === 'last' || it.pos === 'solo');
            const showTime = it.pos === 'last' || it.pos === 'solo';
            return <Bubble key={it.key} msg={it.msg} pos={it.pos} avatar={avatar} name={name}
                           showAvatar={showAvatar} showTime={showTime} />;
          })
        )}
      </div>

      {/* Composer */}
      <Composer
        value={newMessage}
        onChange={setNewMessage}
        onKeyDown={onKeyDown}
        onSend={onSend}
        isSending={isSending}
        placeholder={`Message ${name}…`}
      />
    </div>
  );
}

function clusterize(messages: ChatMessage[]): ThreadItem[] {
  if (!messages.length) return [];

  // First, expand into dated items with date separators.
  const out: ThreadItem[] = [];
  let lastDateKey: string | null = null;
  const dayKey = (ts: number) => {
    const d = new Date(ts);
    return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
  };
  const dayLabel = (ts: number) => {
    const d = new Date(ts);
    const now = new Date();
    const todayKey = dayKey(now.getTime());
    const yesterdayKey = dayKey(now.getTime() - 86400000);
    const k = dayKey(ts);
    if (k === todayKey) return 'Today';
    if (k === yesterdayKey) return 'Yesterday';
    return d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric',
      year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
  };

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    const k = dayKey(msg.timestamp);
    if (k !== lastDateKey) {
      out.push({ type: 'date', key: `d-${k}`, label: dayLabel(msg.timestamp) });
      lastDateKey = k;
    }

    const prev = messages[i - 1];
    const next = messages[i + 1];
    const samePrev = prev && prev.senderId === msg.senderId && dayKey(prev.timestamp) === k;
    const sameNext = next && next.senderId === msg.senderId && dayKey(next.timestamp) === k;
    const pos: ClusterPos =
      samePrev && sameNext ? 'mid' :
      samePrev ? 'last' :
      sameNext ? 'first' : 'solo';

    out.push({ type: 'msg', key: msg.id, msg, pos });
  }

  return out;
}

function Bubble({ msg, pos, avatar, name, showAvatar, showTime }: {
  msg: ChatMessage; pos: ClusterPos;
  avatar: string | null; name: string;
  showAvatar: boolean; showTime: boolean;
}) {
  const me = msg.isOwn;
  const radius = bubbleRadius(me, pos);
  return (
    <div style={{
      display: 'flex', justifyContent: me ? 'flex-end' : 'flex-start',
      alignItems: 'flex-end', gap: 8, padding: '0 22px', marginTop: 4,
    }}>
      {!me && (
        <div style={{ width: 28, flexShrink: 0 }}>
          {showAvatar && <UserAvatar src={avatar} alt={name} size={28} />}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: me ? 'flex-end' : 'flex-start', maxWidth: 520 }}>
        <div style={{
          background: me ? C.brand : C.bubbleThem,
          color: me ? '#fff' : C.ink,
          borderRadius: radius,
          padding: '9px 14px',
          fontSize: 14, lineHeight: 1.4, wordBreak: 'break-word',
          whiteSpace: 'pre-wrap',
        }}>{msg.content}</div>
        {showTime && (
          <span style={{ fontSize: 11, color: C.textFaint, marginTop: 3 }}>
            {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </div>
    </div>
  );
}

function bubbleRadius(me: boolean, pos: ClusterPos): string {
  const big = 18, tight = 6;
  if (me) {
    return pos === 'solo' || pos === 'first'
      ? `${big}px ${big}px ${tight}px ${big}px`
      : `${big}px ${tight}px ${tight}px ${big}px`;
  }
  return pos === 'solo' || pos === 'first'
    ? `${big}px ${big}px ${big}px ${tight}px`
    : `${tight}px ${big}px ${big}px ${tight}px`;
}

function DateSep({ label }: { label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '20px 22px 6px' }}>
      <div style={{ flex: 1, height: 1, background: C.borderSoft }} />
      <span style={{
        fontSize: 11, fontWeight: 600, letterSpacing: '0.04em',
        color: '#8E8E93', textTransform: 'uppercase',
      }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: C.borderSoft }} />
    </div>
  );
}

function UnreadSep() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '18px 22px 4px' }}>
      <div style={{ flex: 1, height: 1, background: C.danger, opacity: 0.3 }} />
      <span style={{
        fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
        color: C.danger, padding: '3px 9px', borderRadius: 99,
        background: 'rgba(255,45,85,0.08)',
      }}>New messages</span>
      <div style={{ flex: 1, height: 1, background: C.danger, opacity: 0.3 }} />
    </div>
  );
}

function ThreadEmpty({ name, avatar }: { name: string; avatar: string | null }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      height: '100%', padding: 40, textAlign: 'center',
    }}>
      <UserAvatar src={avatar} alt={name} size={64} />
      <h3 style={{ margin: '14px 0 6px', fontSize: 16, fontWeight: 700, color: C.ink }}>{name}</h3>
      <p style={{ margin: 0, fontSize: 13.5, color: C.textMuted, maxWidth: 300 }}>
        No messages yet. Say hi or share what you're looking for.
      </p>
    </div>
  );
}

function Composer({ value, onChange, onKeyDown, onSend, isSending, placeholder }: {
  value: string; onChange: (v: string) => void; onKeyDown: (e: React.KeyboardEvent) => void;
  onSend: () => void; isSending: boolean; placeholder: string;
}) {
  const canSend = value.trim().length > 0 && !isSending;
  return (
    <div style={{
      borderTop: `1px solid ${C.border}`, background: C.surface,
      padding: '14px 22px 18px', flexShrink: 0,
    }}>
      <div style={{
        display: 'flex', alignItems: 'flex-end', gap: 10,
        background: C.composerBg, border: '1px solid transparent', borderRadius: 14,
        padding: '8px 8px 8px 14px',
      }}>
        <textarea
          value={value}
          onChange={e => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          rows={1}
          style={{
            flex: 1, border: 'none', outline: 'none', background: 'transparent',
            resize: 'none', fontFamily: 'inherit', fontSize: 14, color: C.ink,
            lineHeight: 1.5, padding: '8px 0', minHeight: 24, maxHeight: 120,
          }}
        />
        <button
          onClick={onSend} disabled={!canSend} title="Send"
          style={{
            width: 36, height: 36, borderRadius: 10, border: 'none',
            background: canSend ? C.brand : '#D8D6E0',
            color: '#fff', cursor: canSend ? 'pointer' : 'not-allowed',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, transition: 'background 0.12s',
          }}
        >
          <Send size={16} />
        </button>
      </div>
      <div style={{ fontSize: 11, color: C.textFaint, marginTop: 6, paddingLeft: 4 }}>
        <Kbd>Enter</Kbd> to send · <Kbd>Shift</Kbd>+<Kbd>Enter</Kbd> for new line
      </div>
    </div>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd style={{
      display: 'inline-block', padding: '1px 5px', borderRadius: 4,
      background: '#fff', border: `1px solid ${C.border}`,
      fontSize: 10, color: '#5C5E6C', fontFamily: 'inherit',
    }}>{children}</kbd>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Profile panel (right rail)
// ──────────────────────────────────────────────────────────────────────
function ProfilePanel({
  profile, fallbackName, fallbackAvatar, participantUserId, onClose,
}: {
  profile: User | null;
  fallbackName: string;
  fallbackAvatar: string | null;
  participantUserId: string | null;
  onClose: () => void;
}) {
  const name = profile?.username || fallbackName || 'User';
  const avatar = profile?.avatarUrl || fallbackAvatar || null;
  const isSeller = profile?.isSeller ?? false;
  const followerCount = profile?.followerCount;
  const presence = useUserPresence(participantUserId);
  const presenceLabel = formatPresence(presence);

  const [isFollowing, setIsFollowing] = useState<boolean>(profile?.isFollowing ?? false);
  const [isMuted, setIsMuted] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [busy, setBusy] = useState(false);

  // Reset state when participant changes; load mute/block status
  useEffect(() => {
    setIsFollowing(profile?.isFollowing ?? false);
    setIsMuted(false);
    setIsBlocked(false);
    if (!participantUserId) return;
    Promise.all([getMutedUsers().catch(() => []), getBlockedUsers().catch(() => [])])
      .then(([mutes, blocks]) => {
        setIsMuted(mutes.some(u => u.id === participantUserId));
        setIsBlocked(blocks.some(u => u.id === participantUserId));
      });
  }, [participantUserId, profile?.isFollowing]);

  const handleFollow = async () => {
    if (!participantUserId) return;
    setBusy(true);
    try {
      const r = isFollowing ? await unfollowUser(participantUserId) : await followUser(participantUserId);
      setIsFollowing(r.following);
    } catch (e) {
      console.error('Follow toggle failed:', e);
    } finally { setBusy(false); }
  };

  const handleMute = async () => {
    if (!participantUserId) return;
    const next = !isMuted;
    setIsMuted(next);
    try {
      if (next) await muteUser(participantUserId); else await unmuteUser(participantUserId);
    } catch (e) {
      console.error('Mute toggle failed:', e);
      setIsMuted(!next);
    }
  };

  const handleBlock = async () => {
    if (!participantUserId) return;
    const next = !isBlocked;
    if (next && !window.confirm(`Block ${name}? They will not be able to interact with you.`)) return;
    setIsBlocked(next);
    try {
      if (next) await blockUser(participantUserId); else await unblockUser(participantUserId);
    } catch (e) {
      console.error('Block toggle failed:', e);
      setIsBlocked(!next);
    }
  };

  const handleReport = async () => {
    if (!participantUserId) return;
    const reason = window.prompt(
      'Report reason — one of:\nSPAM, HARASSMENT, HATE_SPEECH, EXPLICIT_CONTENT, VIOLENCE, FRAUD, PROHIBITED_ITEM, IMPERSONATION, OTHER'
    );
    if (!reason) return;
    const allowed = ['SPAM','HARASSMENT','HATE_SPEECH','EXPLICIT_CONTENT','VIOLENCE','FRAUD','PROHIBITED_ITEM','IMPERSONATION','OTHER'];
    if (!allowed.includes(reason.toUpperCase())) { alert('Invalid reason'); return; }
    const detail = window.prompt('Additional detail (optional):') || undefined;
    try {
      await submitReport({ targetType: 'USER', targetId: participantUserId, reason: reason.toUpperCase() as any, detail });
      alert('Report submitted. Thank you.');
    } catch (e) {
      console.error('Report failed:', e);
      alert('Could not submit report.');
    }
  };

  const formatCount = (n: number | undefined): string => {
    if (n == null) return '—';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`;
    return String(n);
  };

  return (
    <div style={{
      width: 320, flexShrink: 0,
      background: C.surface,
      borderLeft: `1px solid ${C.border}`,
      display: 'flex', flexDirection: 'column',
      overflowY: 'auto', minHeight: 0,
    }}>
      <div style={{ padding: '14px 16px 0', display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={onClose} title="Close" style={{
          width: 28, height: 28, borderRadius: 8, border: 'none',
          background: 'transparent', color: C.textMuted, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <X size={16} />
        </button>
      </div>

      {/* Identity block */}
      <div style={{ padding: '6px 22px 22px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        <div style={{ position: 'relative', marginBottom: 12 }}>
          <UserAvatar src={avatar} alt={name} size={72} />
          {presence.isOnline && (
            <div style={{
              position: 'absolute', right: 0, bottom: 2,
              width: 16, height: 16, borderRadius: 999,
              background: C.online, border: '3px solid #fff', boxSizing: 'border-box',
            }} />
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <span style={{ fontSize: 17, fontWeight: 700, color: C.ink, letterSpacing: '-0.01em' }}>{name}</span>
          {isSeller && <SellerPill />}
        </div>
        {presenceLabel && (
          <div style={{
            fontSize: 12, fontWeight: 500, marginBottom: 16,
            color: presence.isOnline ? C.online : C.textFaint,
          }}>
            {presence.isOnline && (
              <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: 99, background: C.online, marginRight: 5, verticalAlign: 'middle' }} />
            )}
            {presenceLabel}
          </div>
        )}

        {isSeller && (
          <div style={{
            display: 'flex', alignItems: 'stretch',
            background: '#F7F6FA', borderRadius: 12,
            padding: '12px 4px', width: '100%',
            border: '1px solid #ECEBF1', marginBottom: 14,
          }}>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.ink, letterSpacing: '-0.01em' }}>
                {formatCount(followerCount)}
              </div>
              <div style={{ fontSize: 11, color: C.textMuted, marginTop: 1, fontWeight: 500 }}>Followers</div>
            </div>
          </div>
        )}

        <button
          onClick={handleFollow}
          disabled={busy || !participantUserId}
          style={{
            width: '100%', height: 40, border: 'none', borderRadius: 10,
            background: isFollowing ? '#EFEEF3' : C.brand,
            color: isFollowing ? C.brand : '#fff',
            fontSize: 14, fontWeight: 600, cursor: busy ? 'default' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            opacity: busy ? 0.6 : 1,
          }}
        >
          {isFollowing ? <><Check size={16} /> Following</> : <><UserPlus size={16} /> Follow</>}
        </button>
      </div>

      <div style={{ padding: '4px 12px 22px' }}>
        <SectionLabel>Conversation</SectionLabel>
        <PanelRow
          icon={isMuted ? <BellOff size={18} /> : <Bell size={18} />}
          label={isMuted ? 'Unmute notifications' : 'Mute notifications'}
          onClick={handleMute}
        />
        <PanelRow
          icon={<ShieldOff size={18} />}
          label={isBlocked ? 'Unblock' : 'Block user'}
          onClick={handleBlock}
          danger={!isBlocked}
        />
        <PanelRow
          icon={<Flag size={18} />}
          label="Report"
          onClick={handleReport}
        />
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
      color: C.textFaint, textTransform: 'uppercase',
      padding: '10px 10px 6px',
    }}>{children}</div>
  );
}

function PanelRow({ icon, label, onClick, danger }: {
  icon: React.ReactNode; label: string; onClick: () => void; danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 12,
        background: 'transparent', border: 'none',
        padding: '10px 10px', borderRadius: 8, cursor: 'pointer',
        color: danger ? C.danger : C.ink,
        fontSize: 13.5, fontWeight: 500, textAlign: 'left',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = C.hover; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
      <span style={{ color: danger ? C.danger : C.textMuted, display: 'flex' }}>{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
    </button>
  );
}

function SellerPill() {
  return (
    <span style={{
      fontSize: 9.5, fontWeight: 700, letterSpacing: '0.04em',
      padding: '2px 7px', borderRadius: 99,
      background: C.brandSoft, color: C.brand,
      textTransform: 'uppercase', lineHeight: 1,
    }}>SELLER</span>
  );
}

function PresenceDot({ size = 12 }: { size?: number }) {
  return (
    <div style={{
      position: 'absolute', right: 0, bottom: 0,
      width: size, height: size, borderRadius: 999,
      background: C.online, border: '2px solid #fff', boxSizing: 'border-box',
    }} />
  );
}

// ──────────────────────────────────────────────────────────────────────
// Empty + Compose modal
// ──────────────────────────────────────────────────────────────────────
function EmptyThreadHero({ hasConvos }: { hasConvos: boolean }) {
  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: C.surfaceAlt, color: C.textMuted,
    }}>
      <MessageCircle size={64} color="#D8D6E0" />
      <p style={{ marginTop: 16, color: C.text, fontSize: 14 }}>
        {hasConvos ? 'Select a conversation to view messages' : 'No messages yet'}
      </p>
    </div>
  );
}

function ComposeModal({
  query, onQueryChange, results, searching, selected, onSelect, onClearSelected,
  onClose, onStart, starting, error,
}: {
  query: string;
  onQueryChange: (v: string) => void;
  results: UserSearchResult[];
  searching: boolean;
  selected: UserSearchResult | null;
  onSelect: (u: UserSearchResult) => void;
  onClearSelected: () => void;
  onClose: () => void;
  onStart: () => void;
  starting: boolean;
  error: string | null;
}) {
  const canStart = !!selected && !starting;
  const trimmed = query.trim();
  const showEmpty = !selected && trimmed.length < 2;
  const showLoading = !selected && trimmed.length >= 2 && searching;
  const showResults = !selected && trimmed.length >= 2 && !searching && results.length > 0;
  const showNoMatch = !selected && trimmed.length >= 2 && !searching && results.length === 0;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(28,32,51,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: C.surface, borderRadius: 16,
          width: 480, maxWidth: '90vw', overflow: 'hidden',
          boxShadow: '0 24px 60px rgba(28,32,51,0.22), 0 0 0 1px rgba(28,32,51,0.06)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '18px 22px 14px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: `1px solid ${C.borderSoft}`,
        }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, letterSpacing: '-0.01em' }}>New message</h3>
          <button onClick={onClose} title="Close" style={{
            width: 28, height: 28, borderRadius: 8, border: 'none',
            background: 'transparent', color: C.textMuted, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <X size={16} />
          </button>
        </div>

        {/* To row — chip or input */}
        <div style={{ padding: '14px 22px 0' }}>
          <label style={{
            fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
            color: C.textFaint, textTransform: 'uppercase',
            display: 'block', marginBottom: 6,
          }}>To</label>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
            background: C.composerBg, borderRadius: 10,
            padding: selected ? '8px 12px 8px 8px' : '10px 12px',
            border: '1px solid transparent',
          }}>
            {selected ? (
              <ComposeChip user={selected} onRemove={onClearSelected} />
            ) : (
              <>
                <Search size={16} color={C.textFaint} />
                <input
                  autoFocus type="text" value={query}
                  onChange={e => onQueryChange(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && canStart) onStart(); }}
                  placeholder="Search by username or phone number…"
                  style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 14, color: C.ink, minWidth: 0 }}
                />
              </>
            )}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '12px 12px 4px', minHeight: 220, maxHeight: 360, overflowY: 'auto' }}>
          {selected && (
            <div style={{
              margin: '8px 10px 4px', padding: 18,
              background: '#F7F6FA', border: '1px solid #ECEBF1',
              borderRadius: 12,
              display: 'flex', alignItems: 'center', gap: 14,
            }}>
              <UserAvatar src={selected.avatarUrl} alt={selected.username} size={52} />
              <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: C.ink, letterSpacing: '-0.01em' }}>{selected.username}</span>
                {selected.isSeller && <SellerPillSm />}
              </div>
            </div>
          )}

          {showEmpty && (
            <div style={{ padding: '28px 24px 12px', textAlign: 'center' }}>
              <div style={{
                width: 44, height: 44, borderRadius: 12,
                background: C.brandSoft, color: C.brand,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 12,
              }}>
                <MessageSquare size={22} />
              </div>
              <div style={{ fontSize: 14.5, fontWeight: 600, color: C.ink, marginBottom: 4 }}>Find someone to message</div>
              <div style={{ fontSize: 12.5, color: C.textMuted, lineHeight: 1.5, maxWidth: 320, margin: '0 auto' }}>
                Search by Snagg username, or invite someone new by phone number.
              </div>
            </div>
          )}

          {showLoading && (
            <>
              <ComposeSectionLabel>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <ComposeSpinner /> Searching
                </span>
              </ComposeSectionLabel>
              {(['62%','48%','54%'] as const).map((w, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 10px' }}>
                  <div style={{ width: 36, height: 36, borderRadius: 99, background: C.borderSoft }} />
                  <div style={{ height: 12, borderRadius: 4, background: C.borderSoft, width: w }} />
                </div>
              ))}
            </>
          )}

          {showResults && (
            <>
              <ComposeSectionLabel>{results.length} match{results.length > 1 ? 'es' : ''}</ComposeSectionLabel>
              {results.map(p => (
                <button
                  key={p.id}
                  onClick={() => onSelect(p)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                    background: 'transparent', border: 'none',
                    padding: '10px 10px', borderRadius: 8,
                    cursor: 'pointer', textAlign: 'left',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = C.hover; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <UserAvatar src={p.avatarUrl} alt={p.username} size={36} />
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: C.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <ComposeHighlight text={p.username} query={trimmed} />
                    </span>
                    {p.isSeller && <SellerPillSm />}
                  </div>
                </button>
              ))}
            </>
          )}

          {showNoMatch && (
            <div style={{ padding: '24px 10px 8px', textAlign: 'center' }}>
              <div style={{
                width: 44, height: 44, borderRadius: 99, background: C.hover,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                color: C.textFaint, marginBottom: 10,
              }}>
                <Search size={20} />
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.ink, marginBottom: 4 }}>
                No one matched "<span style={{ color: C.brand }}>{trimmed}</span>"
              </div>
              <div style={{ fontSize: 12.5, color: C.textMuted, lineHeight: 1.5 }}>
                Double-check the spelling, or try a phone number.
              </div>
            </div>
          )}

          {error && (
            <div style={{ color: C.danger, fontSize: 13, padding: '6px 12px', textAlign: 'center' }}>{error}</div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 22px 18px',
          borderTop: `1px solid ${C.borderSoft}`,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <span style={{ fontSize: 12, color: C.textFaint, flex: 1 }}>
            {selected ? `Messaging ${selected.username}` : 'Search to find a person'}
          </span>
          <button
            onClick={onStart} disabled={!canStart}
            style={{
              padding: '10px 20px', borderRadius: 10, border: 'none',
              background: canStart ? C.brand : '#D8D6E0',
              color: '#fff', fontSize: 14, fontWeight: 600,
              cursor: canStart ? 'pointer' : 'not-allowed',
            }}
          >
            {starting ? 'Starting…' : 'Start conversation'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ComposeChip({ user, onRemove }: { user: UserSearchResult; onRemove: () => void }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      background: '#fff', border: `1px solid ${C.border}`,
      borderRadius: 99, padding: '4px 6px 4px 4px',
      boxShadow: '0 1px 2px rgba(28,32,51,0.05)',
    }}>
      <UserAvatar src={user.avatarUrl} alt={user.username} size={24} />
      <span style={{ fontSize: 13.5, fontWeight: 600, color: C.ink }}>{user.username}</span>
      {user.isSeller && <SellerPillSm />}
      <button onClick={onRemove} title="Remove" style={{
        width: 18, height: 18, borderRadius: 99, border: 'none',
        background: 'transparent', color: C.textFaint, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <X size={12} />
      </button>
    </div>
  );
}

function ComposeSectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
      color: C.textFaint, textTransform: 'uppercase',
      padding: '8px 10px 4px',
    }}>{children}</div>
  );
}

function ComposeSpinner() {
  return (
    <span style={{
      display: 'inline-block', width: 12, height: 12, borderRadius: 99,
      border: '2px solid #ECEBF1', borderTopColor: C.brand,
      animation: 'nm-spin 0.7s linear infinite',
    }}>
      <style>{`@keyframes nm-spin { to { transform: rotate(360deg); } }`}</style>
    </span>
  );
}

function ComposeHighlight({ text, query }: { text: string; query: string }) {
  if (!query) return <>{text}</>;
  const i = text.toLowerCase().indexOf(query.toLowerCase());
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <mark style={{ background: '#FFF6BA', color: C.ink, padding: 0 }}>{text.slice(i, i + query.length)}</mark>
      {text.slice(i + query.length)}
    </>
  );
}

function SellerPillSm() {
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, letterSpacing: '0.04em',
      padding: '2px 6px', borderRadius: 99,
      background: C.brandSoft, color: C.brand,
      textTransform: 'uppercase', lineHeight: 1,
    }}>SELLER</span>
  );
}
